import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Trash2, Upload, Download } from "lucide-react";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const TYPES = ["resume", "certification", "other"];

export default function PortalDocuments() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ max_size_mb: 8, allowed_extensions: [] });
  const [type, setType] = useState("resume");
  const [busy, setBusy] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/portal/documents");
      setItems(data.items || []);
      setMeta({ max_size_mb: data.max_size_mb, allowed_extensions: data.allowed_extensions || [] });
    } catch (error) {
      /* portal without application yet */
      setItems([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const uploadError = (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.detail?.code;
    const byCode = code && t(`documents.errCode.${code}`);
    if (byCode && !byCode.startsWith("documents.")) return byCode;
    if (status === 415) return t("documents.errCode.INVALID_FILE_TYPE");
    if (status === 413) return t("documents.errCode.FILE_TOO_LARGE");
    if (status === 422) return t("documents.errCode.EMPTY_FILE");
    if (status === 503) return t("documents.errCode.STORAGE_UNAVAILABLE");
    if (status === 401 || status === 403) return t("documents.errCode.UNAUTHORIZED");
    if (status === 404) return t("documents.errCode.CANDIDATE_NOT_FOUND");
    return t("documents.errCode.UPLOAD_FAILED");
  };

  const clientCheck = (file) => {
    const extension = (file.name.split(".").pop() || "").toLowerCase();
    const allowed = meta.allowed_extensions?.length
      ? meta.allowed_extensions
      : ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
    if (!allowed.includes(extension)) return t("documents.errCode.INVALID_FILE_TYPE");
    if (file.size === 0) return t("documents.errCode.EMPTY_FILE");
    if (file.size > (meta.max_size_mb || 8) * 1024 * 1024) return t("documents.errCode.FILE_TOO_LARGE");
    return null;
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const clientError = clientCheck(file);
    if (clientError) {
      setUploadMessage(clientError);
      toast.error(clientError);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post(`/portal/documents?document_type=${type}`, body);
      toast.success(t("documents.uploaded"));
      setUploadMessage("");
      load();
    } catch (error) {
      const message = uploadError(error);
      setUploadMessage(message);
      toast.error(message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/portal/documents/${id}`);
      toast.success(t("documents.deleted"));
      setUploadMessage("");
      load();
    } catch (error) {
      toast.error(t("documents.failed"));
    }
  };

  const download = async (doc) => {
    try {
      const response = await api.get(`/portal/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = doc.original_filename;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t("documents.failed"));
    }
  };

  return (
    <section data-testid="portal-documents" className="border border-border bg-surface p-6">
      <h2 className="font-display text-2xl">{t("documents.title")}</h2>
      <p className="mt-2 text-xs text-muted-foreground">
        {t("documents.rules")} · {meta.allowed_extensions.join(", ").toUpperCase()} · {meta.max_size_mb} {t("documents.sizeUnit")}
      </p>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <select
          data-testid="document-type-select"
          value={type}
          onChange={(event) => setType(event.target.value)}
          className="h-10 w-full border border-input bg-surface px-3 text-sm sm:w-56"
          aria-label={t("documents.type")}
        >
          {TYPES.map((key) => (
            <option key={key} value={key}>
              {t(`documents.types.${key}`)}
            </option>
          ))}
        </select>
        <input
          ref={inputRef}
          type="file"
          data-testid="document-file-input"
          onChange={upload}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          className="hidden"
        />
        <Button
          type="button"
          data-testid="document-upload-button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 sm:w-auto"
        >
          <Upload className="mr-2 h-4 w-4" />
          {busy ? t("common.loading") : t("documents.upload")}
        </Button>
      </div>

      {uploadMessage && (
        <p data-testid="document-upload-error" role="alert" className="mt-4 text-sm text-destructive">
          {uploadMessage}
        </p>
      )}

      <ul data-testid="documents-list" className="mt-6 divide-y divide-border border-t border-border">        {items.length === 0 && (
          <li data-testid="documents-empty" className="py-4 text-sm text-muted-foreground">
            {t("documents.empty")}
          </li>
        )}
        {items.map((doc) => (
          <li key={doc.id} data-testid={`document-row-${doc.id}`} className="flex flex-wrap items-center gap-3 py-4">
            <FileText className="h-4 w-4 shrink-0 text-accent" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{doc.original_filename}</p>
              <p className="text-xs text-muted-foreground">
                {t(`documents.types.${doc.document_type}`)} · {(doc.size_bytes / 1024).toFixed(0)} KB ·{" "}
                {(doc.uploaded_at || "").slice(0, 10)}
              </p>
            </div>
            <button
              type="button"
              data-testid={`document-download-${doc.id}`}
              onClick={() => download(doc)}
              className="text-xs text-accent hover:underline"
            >
              <Download className="mr-1 inline h-3.5 w-3.5" />
              {t("documents.download")}
            </button>
            <button
              type="button"
              data-testid={`document-delete-${doc.id}`}
              onClick={() => remove(doc.id)}
              className="text-xs text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="mr-1 inline h-3.5 w-3.5" />
              {t("documents.delete")}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
